import NotificationService from '@/services/notification'
import BusinessService from '@/services/business'
import UserService from '@/services/user'

import router from '@/router'
import i18n from '@/helpers/i18n'
import { decodeJwt } from '@/helpers/jwt'

import { VUE_APP_ROOT_API } from '@/helpers/env'

import { URL_ROOT } from '@/consts/urls'
import { USER_TYPE_USER, USER_TYPE_SYSTEM_ADMIN, USER_TYPE_ANONYME } from '@/consts/enums'
import { CLAIM_UTYPE_KEY, CLAIM_UTYPE_USER, CLAIM_UTYPE_SYSTEMADMIN } from '@/consts/claims'
import {
  LOCAL_STORAGE_AUTHTOKEN,
  LOCAL_STORAGE_DEVICEID,
  LOCAL_STORAGE_REFRESHTOKEN,
  LOCAL_STORAGE_RENEWTOKEN_STATUS
} from '@/consts/local-storage'

export default {
  login: async function(username, password, loginResolver = null) {
    await BusinessService.emptyLocalState()
    let deviceId = localStorage.getItem(LOCAL_STORAGE_DEVICEID)

    if (deviceId === null) {
      deviceId = 'aemq-' + new Date().getTime()
      localStorage.setItem(LOCAL_STORAGE_DEVICEID, deviceId)
    }

    const response = await fetch(`${VUE_APP_ROOT_API}/token/login`, {
      method: 'post',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ username, password, deviceId })
    })

    if (response.status === 200) {
      const result = await response.json()
      await setUser(result.token, result.refreshToken)

      if (loginResolver !== null && typeof loginResolver === 'function') {
        loginResolver()
      } else {
        router
          .push(router.currentRoute.query.returnPath || { name: URL_ROOT })
          // https://stackoverflow.com/a/63017565
          .catch(e => {
            // eslint-disable-next-line
            console.warn(e.message)
          })
      }
    } else {
      NotificationService.showError(i18n.t('login-error-notification'))
    }
  },
  renewToken: async function() {
    if (localStorage.getItem(LOCAL_STORAGE_RENEWTOKEN_STATUS)) {
      await waitRefreshToken()
      return
    }

    localStorage.setItem(LOCAL_STORAGE_RENEWTOKEN_STATUS, 'active')

    const requestData = {
      token: localStorage.getItem(LOCAL_STORAGE_AUTHTOKEN),
      refreshToken: localStorage.getItem(LOCAL_STORAGE_REFRESHTOKEN),
      deviceId: localStorage.getItem(LOCAL_STORAGE_DEVICEID)
    }

    const response = await fetch(`${VUE_APP_ROOT_API}/token/refresh`, {
      method: 'post',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestData)
    })

    if (response.status === 200) {
      let result = await response.json()
      await setUser(result.token, result.refreshToken)
    } else {
      await logout()
    }

    localStorage.removeItem(LOCAL_STORAGE_RENEWTOKEN_STATUS)
  },
  logout: logout,
  loadLocalState: async function() {
    let token = localStorage.getItem(LOCAL_STORAGE_AUTHTOKEN)
    let refreshToken = localStorage.getItem(LOCAL_STORAGE_REFRESHTOKEN)
    localStorage.removeItem(LOCAL_STORAGE_RENEWTOKEN_STATUS)

    if (token !== null && refreshToken !== null) {
      await setUser(token, refreshToken)
    }
  },
  getUserIsLogged() {
    return UserService.isConnected() && this.getUserType() !== USER_TYPE_ANONYME
  },
  getUserType: function() {
    if (!UserService.isConnected()) return USER_TYPE_ANONYME

    const token = this.getUserToken()
    const claims = decodeJwt(token)
    const utype = (claims && claims[CLAIM_UTYPE_KEY]) || USER_TYPE_ANONYME

    switch (utype) {
      case CLAIM_UTYPE_USER:
        return USER_TYPE_USER
      case CLAIM_UTYPE_SYSTEMADMIN:
        return USER_TYPE_SYSTEM_ADMIN
      default:
        return utype
    }
  },
  getUserToken: function() {
    return UserService.getAccessToken()
  },
  getUserRefreshToken: function() {
    return UserService.getRefreshToken()
  }
}

function uTypeToGraphqlType(utype) {
  let type = USER_TYPE_ANONYME
  switch (utype) {
    case 'SystemAdmin':
      type = USER_TYPE_SYSTEM_ADMIN
      break
    case 'User':
      type = USER_TYPE_USER
      break
  }

  return type
}

async function setUser(accessToken, refreshToken) {
  localStorage.setItem(LOCAL_STORAGE_AUTHTOKEN, accessToken)
  localStorage.setItem(LOCAL_STORAGE_REFRESHTOKEN, refreshToken)

  let userToken = decodeJwt(accessToken)

  await UserService.setLocalUser(
    true, // isConnected
    uTypeToGraphqlType(userToken[CLAIM_UTYPE_KEY]),
    accessToken,
    refreshToken
  )
}

async function waitRefreshToken() {
  return new Promise((resolve, reject) => {
    let timerId = setInterval(function() {
      if (!localStorage.getItem(LOCAL_STORAGE_RENEWTOKEN_STATUS)) {
        clearInterval(timerId)
        resolve()
      }
    }, 50)
  })
}

async function logout() {
  let emptyLocalStatePromise = BusinessService.emptyLocalState()

  localStorage.removeItem(LOCAL_STORAGE_AUTHTOKEN)
  localStorage.removeItem(LOCAL_STORAGE_REFRESHTOKEN)

  await emptyLocalStatePromise
  window.location.href = '/'
}
